import { useState, useEffect, useRef, useMemo } from 'react';
import { getValidToken } from '../utils/auth';
import { useMarketContext } from '../context/MarketDataContext';
import TradingDatePicker from './TradingDatePicker';
import { PRESET_TICKERS } from '../utils/tickers';
import { nextTradingDay, toDateStr, etToday } from '../utils/tradingCalendar';

// Reconciliation cadence: fetch/re-fetch the base contract list + subscribe
// it onto the backend's WS streams (see backend/handlers/generic_options.go).
// This is NOT the primary refresh mechanism anymore - live price/volume/spot
// updates arrive continuously over the existing /ws connection (option_tick,
// underlying_trade) via useMarketContext(). This fetch only needs to happen
// once per symbol/date selection plus an infrequent backstop, matching the
// backend's own reconcileToday/reconcileOther cadence, to catch a cold start
// or a message dropped across a reconnect gap.
const RECONCILE_MS_TODAY = 60000;
const RECONCILE_MS_OTHER = 300000;

const API_URL = process.env.REACT_APP_API_URL || '';

function todayET() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
}

function calcDTE(date) {
  const msPerDay = 86400000;
  return Math.round((new Date(date + 'T00:00:00') - new Date(todayET() + 'T00:00:00')) / msPerDay);
}

// customTickers and onCustomTickersChange are owned by Options.js so that
// ChartWidget and GenericOptionsWidget always see the same list.
export default function GenericOptionsWidget({ onContractSelect, customTickers = [], onCustomTickersChange }) {
  const allTickers = [...PRESET_TICKERS, ...customTickers.filter(t => !PRESET_TICKERS.includes(t))];
  const { optionTicks, underlyingTicks, connected: wsConnected, marketStatus } = useMarketContext();

  const [symbol, setSymbol] = useState('SPX');
  // Default to next trading day from today — so on weekends/holidays the widget
  // opens on a valid session instead of showing "No contracts found".
  const [selectedDate, setSelectedDate] = useState(() => toDateStr(nextTradingDay(etToday())));
  // Dates with an actual option expiration for the current symbol (null =
  // not loaded yet, so the date picker falls back to "any trading day").
  // Some symbols (NVDA, most single stocks) only expire weekly/monthly, not
  // daily like SPY/SPX/QQQ, so picking an arbitrary trading day can land on
  // one with nothing listed.
  const [expirations, setExpirations] = useState(null);
  // Base contract list from the one-time (+ infrequent backstop) REST fetch.
  // Live per-symbol overlays from optionTicks/underlyingTicks are merged on
  // top of this in the `contracts`/`underlyingPrice` useMemos below.
  const [baseContracts, setBaseContracts] = useState(null);
  const [baseUnderlyingPrice, setBaseUnderlyingPrice] = useState(null);
  const [sort, setSort] = useState({ col: 'volume', dir: 'desc' });
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'C' | 'P'
  const [connStatus, setConnStatus] = useState('connecting');
  const [selectedSymbol, setSelectedSymbol] = useState(null); // for row highlight
  const selectedSymbolRef = useRef(null); // mirrors selectedSymbol for the poll closure
  useEffect(() => { selectedSymbolRef.current = selectedSymbol; }, [selectedSymbol]);

  // Strike search
  const [searchOpen, setSearchOpen] = useState(false);
  const [strikeSearch, setStrikeSearch] = useState('');
  const searchInputRef = useRef(null);
  useEffect(() => { if (searchOpen && searchInputRef.current) searchInputRef.current.focus(); }, [searchOpen]);
  const closeSearch = () => { setSearchOpen(false); setStrikeSearch(''); };

  // + button state
  const [addingTicker, setAddingTicker] = useState(false);
  const [newTickerInput, setNewTickerInput] = useState('');
  const addInputRef = useRef(null);

  // Focus the add-ticker input when it appears
  useEffect(() => {
    if (addingTicker && addInputRef.current) {
      addInputRef.current.focus();
    }
  }, [addingTicker]);

  const commitNewTicker = () => {
    const t = newTickerInput.trim().toUpperCase();
    if (t && !allTickers.includes(t)) {
      onCustomTickersChange?.([...customTickers, t]);
      setSymbol(t);
    } else if (t && allTickers.includes(t)) {
      setSymbol(t);
    }
    setAddingTicker(false);
    setNewTickerInput('');
  };

  // Fetch which dates actually have an expiration for the selected symbol,
  // and snap off a now-invalid selectedDate onto the nearest valid one.
  useEffect(() => {
    let mounted = true;
    setExpirations(null);
    (async () => {
      try {
        const token = await getValidToken();
        const res = await fetch(`${API_URL}/api/options/expirations?symbol=${symbol}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!mounted) return;
        const set = new Set(data.expirations || []);
        setExpirations(set);
        if (set.size > 0 && !set.has(selectedDate)) {
          const nextValid = [...set].filter(d => d >= selectedDate).sort()[0] || [...set].sort()[0];
          if (nextValid) setSelectedDate(nextValid);
        }
      } catch {
        if (mounted) setExpirations(null); // fall back to "any trading day" on failure
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  // Fetch the base contract list once per symbol/date selection - this same
  // request also tells the backend to subscribe every returned contract (and
  // the underlying) onto its WS streams, so live updates start arriving over
  // /ws from here on. Re-fetched on a slow backstop cadence only (see
  // RECONCILE_MS_* above), not as the primary refresh path.
  useEffect(() => {
    let mounted = true;
    let backstopTimer = null;

    setBaseContracts(null);
    setConnStatus('connecting');
    setSelectedSymbol(null);
    setTypeFilter('all');
    closeSearch();

    const url = `${API_URL}/api/options?symbol=${symbol}&date=${selectedDate}`;

    async function fetchOnce() {
      try {
        const token = await getValidToken();
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (mounted) {
          setBaseContracts(data.contracts);
          setBaseUnderlyingPrice(data.spy_price);
          setConnStatus('live');
        }
      } catch {
        if (mounted) setConnStatus('reconnecting');
      }
    }

    const backstop = selectedDate === todayET() ? RECONCILE_MS_TODAY : RECONCILE_MS_OTHER;
    fetchOnce();
    backstopTimer = setInterval(fetchOnce, backstop);

    return () => {
      mounted = false;
      clearInterval(backstopTimer);
    };
  }, [symbol, selectedDate]);

  // Live-merge WS ticks onto the base contract list - overlays bid/ask
  // midpoint (or last trade, whichever's available) plus cumulative volume
  // per contract, recomputing change% from the REST-supplied prev_close
  // baseline so it stays consistent without another round-trip.
  //
  // Session-aware price source: mirrors the same asymmetry the backend's own
  // REST fetch already accounts for (handlers/spy_options.go's
  // inRegularSession gate) - outside 9:30-16:00 ET, Alpaca's indicative
  // quote feed freezes at the prior value while SPX/SPXW trades keep
  // printing (Cboe's near-24h Global Trading Hours). Without this, SPX rows
  // would silently show a stale frozen mid-quote overnight instead of the
  // live trade price, even though real ticks are arriving.
  const inRegularSession = marketStatus?.status === 'market';
  const contracts = useMemo(() => {
    if (!baseContracts) return null;
    return baseContracts.map(c => {
      const tick = optionTicks[c.symbol];
      if (!tick) return c;
      let price = c.price;
      if (!inRegularSession && tick.last > 0) price = tick.last;
      else if (tick.bid > 0 && tick.ask > 0) price = (tick.bid + tick.ask) / 2;
      else if (tick.last > 0) price = tick.last;
      const volume = tick.volume > 0 ? tick.volume : c.volume;
      const changePct = c.prev_close > 0 && price > 0
        ? (price - c.prev_close) / c.prev_close * 100
        : c.change_pct;
      return { ...c, price, volume, change_pct: changePct };
    });
  }, [baseContracts, optionTicks, inRegularSession]);

  // SPX has no direct spot feed - the underlying tick stream subscribes "SPY"
  // for it (see backend's fetchSym mapping) and we apply the same ×10+35
  // proxy client-side that the backend applies server-side for the REST value.
  const underlyingPrice = useMemo(() => {
    const tickSym = symbol === 'SPX' ? 'SPY' : symbol;
    const live = underlyingTicks[tickSym];
    if (live == null) return baseUnderlyingPrice;
    return symbol === 'SPX' ? live * 10 + 35 : live;
  }, [symbol, underlyingTicks, baseUnderlyingPrice]);

  // Keep the selected contract's chart live off the same merged data,
  // instead of only pushing a fresh price at click time.
  useEffect(() => {
    if (!contracts || !selectedSymbolRef.current || !onContractSelect) return;
    const match = contracts.find(c => c.symbol === selectedSymbolRef.current);
    if (match) onContractSelect(match);
  }, [contracts, onContractSelect]);

  // Auto-select first contract (by volume) when contracts first load for a new symbol/date
  const autoSelectedRef = useRef(false);
  useEffect(() => { autoSelectedRef.current = false; }, [symbol, selectedDate]);
  useEffect(() => {
    if (!contracts || contracts.length === 0 || autoSelectedRef.current) return;
    const first = [...contracts].sort((a, b) => b.volume - a.volume)[0];
    autoSelectedRef.current = true;
    setSelectedSymbol(first.symbol);
    if (onContractSelect) onContractSelect(first);
  }, [contracts, onContractSelect]);

  const handleRowClick = (c) => {
    setSelectedSymbol(c.symbol);
    if (onContractSelect) onContractSelect(c);
  };

  const toggleSort = (col) =>
    setSort(s => s.col === col ? { col, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { col, dir: 'desc' });

  const sorted = useMemo(() => {
    if (!contracts) return null;
    const key = { volume: 'volume', price: 'price', change_pct: 'change_pct', strike: 'strike' }[sort.col] || 'volume';
    return [...contracts].sort((a, b) =>
      sort.dir === 'desc' ? b[key] - a[key] : a[key] - b[key]
    );
  }, [contracts, sort]);

  // Filter sorted contracts by type and strike search
  const filtered = useMemo(() => {
    if (!sorted) return null;
    let result = sorted;
    if (typeFilter !== 'all') result = result.filter(c => c.type === typeFilter);
    if (strikeSearch) result = result.filter(c => String(c.strike).includes(strikeSearch));
    return result;
  }, [sorted, strikeSearch, typeFilter]);

  // "Live" now means both the base fetch succeeded AND the WS connection
  // that carries every price/volume update after that is actually up -
  // showing "Live" while /ws is down would be misleading (the table would
  // silently stop updating without this check reflecting it).
  const effectiveStatus = connStatus === 'live' && !wsConnected ? 'reconnecting' : connStatus;
  const dotColor = effectiveStatus === 'live' ? '#22c55e' : effectiveStatus === 'reconnecting' ? '#f59e0b' : '#6b7280';
  const dotLabel = effectiveStatus === 'live' ? 'Live' : effectiveStatus === 'reconnecting' ? 'Reconnecting…' : 'Connecting…';

  const SortArrow = ({ col }) =>
    sort.col === col
      ? <span className="ml-1" style={{ color: '#60a5fa' }}>{sort.dir === 'desc' ? '↓' : '↑'}</span>
      : <span className="ml-1 text-gray-600">↕</span>;

  const dte = calcDTE(selectedDate);
  const dteColor = dte === 0 ? '#22c55e' : dte < 0 ? '#ef4444' : '#f59e0b';

  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">
      {/* Header row */}
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">

        {/* LEFT: ticker dropdown + add button */}
        <div className="flex items-center gap-2">
          <div className="relative">
            <select
              value={symbol}
              onChange={e => setSymbol(e.target.value)}
              className="appearance-none text-xs font-bold rounded px-3 py-1.5 pr-7 border cursor-pointer focus:outline-none focus:border-blue-500"
              style={{ backgroundColor: '#111827', color: '#e5e7eb', borderColor: '#374151' }}
            >
              {allTickers.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            {/* custom chevron */}
            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">▾</span>
          </div>

          {addingTicker ? (
            <div className="flex items-center gap-1">
              <input
                ref={addInputRef}
                value={newTickerInput}
                onChange={e => setNewTickerInput(e.target.value.toUpperCase())}
                onKeyDown={e => {
                  if (e.key === 'Enter') commitNewTicker();
                  if (e.key === 'Escape') { setAddingTicker(false); setNewTickerInput(''); }
                }}
                placeholder="TICKER"
                maxLength={6}
                className="text-xs rounded px-2 py-1 border focus:outline-none focus:border-blue-500 w-20 uppercase"
                style={{ backgroundColor: '#1f2937', color: '#e5e7eb', borderColor: '#374151' }}
              />
              <button
                onClick={commitNewTicker}
                className="text-xs rounded px-2 py-1 font-bold"
                style={{ backgroundColor: '#22c55e22', color: '#22c55e' }}
              >✓</button>
              <button
                onClick={() => { setAddingTicker(false); setNewTickerInput(''); }}
                className="text-xs rounded px-2 py-1"
                style={{ backgroundColor: '#374151', color: '#9ca3af' }}
              >✕</button>
            </div>
          ) : (
            <button
              onClick={() => setAddingTicker(true)}
              title="Add custom ticker"
              className="w-7 h-7 flex items-center justify-center rounded border text-sm font-bold transition-colors"
              style={{ backgroundColor: '#1f2937', color: '#60a5fa', borderColor: '#374151' }}
            >+</button>
          )}
        </div>

        {/* CENTER: date picker + DTE badge */}
        <div className="flex items-center gap-2">
          <TradingDatePicker value={selectedDate} onChange={setSelectedDate} allowedDates={expirations} />
          <span
            className="text-xs font-semibold px-2 py-0.5 rounded"
            style={{ backgroundColor: dteColor + '22', color: dteColor }}
          >
            {dte} DTE
          </span>
        </div>

        {/* RIGHT: underlying price + connection status */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-bold" style={{ color: '#f9fafb' }}>{symbol}</span>
            {underlyingPrice === null
              ? <span className="w-16 h-4 bg-gray-800 rounded animate-pulse inline-block align-middle" />
              : underlyingPrice > 0
                ? <span className="text-sm font-bold tabular-nums" style={{ color: '#60a5fa' }}>
                    ${underlyingPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                : <span className="text-sm text-gray-500">—</span>
            }
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: dotColor }} />
            <span className="text-xs text-gray-400">{dotLabel}</span>
          </div>
        </div>
      </div>

      {/* Loading skeleton */}
      {sorted === null && (
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-7 bg-gray-800 rounded animate-pulse" />
          ))}
        </div>
      )}

      {/* Empty state — no contracts for this symbol/date */}
      {sorted !== null && sorted.length === 0 && (() => {
        const d = new Date(selectedDate + 'T12:00:00');
        const dow = d.getDay();
        const isWeekend = dow === 0 || dow === 6;
        return (
          <div className="text-center py-8 text-gray-500 text-sm">
            {isWeekend
              ? `No contracts — ${d.toLocaleDateString('en-US', { weekday: 'long' })} is not a trading day`
              : `No contracts found for ${symbol} on this date`}
          </div>
        );
      })()}

      {/* Contracts table — shown whenever sorted has rows.
          The thead (which contains the search input) is always visible so the
          user can clear a no-match search without losing access to the ✕ button.
          The "no matches" message lives inside tbody instead of outside the table. */}
      {sorted !== null && sorted.length > 0 && (
        <div className="overflow-auto" style={{ maxHeight: 400 }}>
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-900">
              <tr>
                <th className="text-left pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider w-12 sm:w-28">
                  <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-1">
                    {[
                      { value: 'all',  label: 'All',  activeColor: '#60a5fa' },
                      { value: 'C',    label: 'Call', activeColor: '#22c55e' },
                      { value: 'P',    label: 'Put',  activeColor: '#ef4444' },
                    ].map(({ value, label, activeColor }) => {
                      const active = typeFilter === value;
                      return (
                        <button
                          key={value}
                          onClick={() => setTypeFilter(value)}
                          className="text-xs font-semibold px-1.5 py-0.5 rounded transition-colors"
                          style={{
                            backgroundColor: active ? activeColor + '22' : 'transparent',
                            color:           active ? activeColor         : '#6b7280',
                            border:          `1px solid ${active ? activeColor + '55' : 'transparent'}`,
                          }}
                        >{label}</button>
                      );
                    })}
                  </div>
                </th>
                <th className="text-left pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center gap-1.5">
                    {searchOpen ? (
                      <>
                        <input
                          ref={searchInputRef}
                          value={strikeSearch}
                          onChange={e => setStrikeSearch(e.target.value.replace(/[^0-9.]/g, ''))}
                          onKeyDown={e => e.key === 'Escape' && closeSearch()}
                          placeholder="strike…"
                          className="text-xs rounded px-2 py-0.5 border focus:outline-none focus:border-blue-500 w-24 font-normal normal-case tracking-normal"
                          style={{ backgroundColor: '#1f2937', color: '#e5e7eb', borderColor: '#374151' }}
                        />
                        <button onClick={closeSearch} title="Clear search"
                          style={{ color: '#6b7280' }} className="hover:text-gray-300 leading-none">✕</button>
                      </>
                    ) : (
                      <>
                        <span className="cursor-pointer hover:text-gray-200" onClick={() => toggleSort('strike')}>
                          Contract <SortArrow col="strike" />
                        </span>
                        <button onClick={() => setSearchOpen(true)} title="Search by strike"
                          className="hover:text-gray-200 transition-colors" style={{ color: '#6b7280' }}>
                          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <circle cx="6.5" cy="6.5" r="4.5" />
                            <line x1="10.5" y1="10.5" x2="14" y2="14" />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                </th>
                <th
                  className="text-right pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-200"
                  onClick={() => toggleSort('price')}
                >Price <SortArrow col="price" /></th>
                <th
                  className="text-right pb-2 pr-3 text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-200"
                  onClick={() => toggleSort('change_pct')}
                >Chg% <SortArrow col="change_pct" /></th>
                <th
                  className="text-right pb-2 text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-200"
                  onClick={() => toggleSort('volume')}
                >Volume <SortArrow col="volume" /></th>
              </tr>
            </thead>
            <tbody>
              {/* No-match row — keeps thead (and ✕ button) visible so user can clear search */}
              {filtered !== null && filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-sm" style={{ color: '#6b7280' }}>
                    No contracts matching
                    {typeFilter !== 'all' && <span style={{ color: typeFilter === 'C' ? '#22c55e' : '#ef4444' }}> {typeFilter === 'C' ? 'CALL' : 'PUT'}</span>}
                    {strikeSearch && <span> strike <span style={{ color: '#60a5fa' }}>{strikeSearch}</span></span>}
                    {' — '}
                    <button
                      onClick={() => { setTypeFilter('all'); closeSearch(); }}
                      className="underline hover:text-gray-300 transition-colors"
                      style={{ color: '#9ca3af' }}
                    >clear filters</button>
                  </td>
                </tr>
              )}
              {filtered !== null && filtered.map(c => {
                const isCall = c.type === 'C';
                const chgColor = c.change_pct >= 0 ? '#22c55e' : '#ef4444';
                const tvBase = c.symbol.slice(0, -9);
                const tvStrike = Number.isInteger(c.strike) ? c.strike.toFixed(1) : c.strike.toString();
                const tvLink = `https://www.tradingview.com/chart/GYtOhxAt/?symbol=OPRA%3A${tvBase}${c.type}${tvStrike}`;
                const isSelected = c.symbol === selectedSymbol;
                return (
                  <tr
                    key={c.symbol}
                    onClick={() => handleRowClick(c)}
                    className="border-t border-gray-800 cursor-pointer"
                    style={isSelected
                      ? { backgroundColor: 'rgba(96,165,250,0.08)', borderLeft: '2px solid #60a5fa' }
                      : { borderLeft: '2px solid transparent' }
                    }
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)'; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.backgroundColor = ''; }}
                  >
                    <td className="py-1.5 pr-3">
                      <span
                        className="px-1.5 py-0.5 rounded text-xs font-semibold"
                        style={{
                          backgroundColor: isCall ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                          color: isCall ? '#22c55e' : '#ef4444',
                        }}
                      >{isCall ? 'CALL' : 'PUT'}</span>
                    </td>
                    <td className="py-1.5 pr-3">
                      <a
                        href={tvLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-xs hover:text-blue-300"
                        style={{ color: '#60a5fa' }}
                      >${c.strike % 1 === 0 ? c.strike.toFixed(0) : c.strike.toFixed(1)}</a>
                    </td>
                    <td className="py-1.5 pr-3 text-right font-mono text-gray-200">${c.price.toFixed(2)}</td>
                    <td className="py-1.5 pr-3 text-right font-mono font-semibold" style={{ color: chgColor }}>
                      {c.change_pct >= 0 ? '+' : ''}{c.change_pct.toFixed(1)}%
                    </td>
                    <td className="py-1.5 text-right font-mono text-gray-300">{c.volume.toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
